<template>
	<div
		class="checkout-success"
		v-if="!loading"
	>
		<template v-if="tokenValue">
			<template v-if="isOrderTypeMain || isOrderTypeCourse || isOrderTypeVoucher || isOrderTypeVoucherReload || isOrderTypeGastroKurier">
				<h1
					class="checkout-success__headline"
					v-html="$t('checkout.success.title')"
				/>
				<LiveOrderStates
          v-if="hasFeature('FEATURE_LIVE_ORDER_STATE')"
					:tokenValues="[tokenValue]"
				/>
				<h2
					class="checkout-success__subheadline"
					v-html="$t('checkout.success.subTitle')"
				/>
			</template>
			<template v-else-if="isOrderTypeReusableDishes">
				<h1
					class="checkout-success__headline"
					v-html="$t('checkout.success.titleDishes')"
				/>
				<h2
					class="checkout-success__subheadline"
					v-html="$t('checkout.success.subTitleDishes')"
				/>
			</template>
			<template v-else-if="isOrderTypeTip">
				<h1
					class="checkout-success__headline"
					v-html="$t('checkout.success.titleTip')"
				/>
				<h2
					class="checkout-success__subheadline"
					v-html="$t('checkout.success.subTitleTip')"
				/>
			</template>
			<ul class="checkout-success__info">
				<li
					v-if="!isOrderTypeVoucher && !isOrderTypeVoucherReload"
					class="checkout-success__info-item"
				>
					<SVGLocation/>
					<span v-html="channelName"/>
				</li>
				<li class="checkout-success__info-item">
					<SVGDate/>
					<span v-html="deliveryDate"/>
				</li>
				<li
					class="checkout-success__info-item"
					v-if="!timeDisabled"
				>
					<SVGClock/>
					<span v-html="deliveryTime"/>
				</li>
				<li
					v-if="shippingMethod && !isOrderTypeTip"
					class="checkout-success__info-item"
				>
					<component
						v-if="shippingMethod && shippingMethod.code"
						:is="shippingMethodIconComponent(shippingMethod.code)"
					/>
					<span v-html="shippingMethod.name"/>
				</li>
				<li
					v-if="!paymentDisabled"
					v-for="(payment, i) in payments"
					:key="`payment-${i}`"
					class="checkout-success__info-item"
				>
					<component
						:is="iconComponent(payment.method.code) || iconComponent('datatrans')"
					/>
					<span v-html="payment.method.name"/>
				</li>
			</ul>

			<template v-if="isOrderTypeMain || isOrderTypeCourse || isOrderTypeVoucher || isOrderTypeVoucherReload || isOrderTypeReusableDishes || isOrderTypeGastroKurier">
				<template v-if="!isOutdoorDelivery">
					<div
						class="checkout-success__address"
						v-if="!shippingAddressDisabled || !billingAddressDisabled"
					>
						<div
							v-if="!billingAddressDisabled"
							class="checkout-success__address-billing"
						>
							<h3
								class="checkout-success__title"
								v-html="$t('checkout.success.billingAddress')"
							/>
							<address v-sanitize="formatAddress(billingAddress)"/>
						</div>
						<div
							v-if="!shippingAddressDisabled && isDelivery"
							class="checkout-success__address-shipping"
						>
							<h3
								class="checkout-success__title"
								v-html="$t('checkout.success.shippingAddress')"
							/>
							<address v-sanitize="formatAddress(shippingAddress)"/>
						</div>
					</div>
				</template>
				<template v-else>
					<div
						class="checkout-success__address"
					>
						<div
							class="checkout-success__address-outdoor"
						>
							<h3
								class="checkout-success__title"
								v-html="$t('checkout.success.shippingAddress')"
							/>
							<div class="checkout-form__outdoor-delivery">
								<figure
									v-if="cartOutdoorDeliveryPoint.image && cartOutdoorDeliveryPoint.image.path"
									class="checkout-form__outdoor-delivery-image checkout-form__outdoor-delivery-image--full"
								>
									<img
										class="checkout-form__outdoor-delivery-image-element"
										:src="cartOutdoorDeliveryPoint.image.path"
										alt="outdoor delivery point image"
									/>
								</figure>
								<address
									class="checkout-form__outdoor-delivery-address"
									v-sanitize="outdoorDeliveryAddress"
								/>
								<Button
									class="checkout-form__outdoor-delivery-link"
									type="button"
									:href="`http://www.google.com/maps/place/${cartOutdoorDeliveryPoint.latitude},${cartOutdoorDeliveryPoint.longitude}`"
									target="_blank"
									level="tertiary"
									arrow
									:text="$t('checkout.outdoorDelivery.googleMapsLink')"
								/>
							</div>

							<h3
								class="checkout-success__title"
								v-html="$t('checkout.personalData')"
							/>
							<address
								v-sanitize="formatAddress({firstName: shippingAddress.firstName, lastName: shippingAddress.lastName, phoneNumber: shippingAddress.phoneNumber})"
							/>
						</div>
					</div>
				</template>

				<template v-if="dropoffType && !dropoffDisabled">
					<h3
						class="checkout-success__title"
						v-html="$t('checkout.dropoff.title')"
					/>
					<div
						class="checkout-success__text"
						v-html="$t(`checkout.dropoff.${dropoffType}`)"
					/>
				</template>

				<template v-if="notes && !notesDisabled">
					<h3
						class="checkout-success__title"
						v-html="$t('checkout.success.notes')"
					/>
					<div
						class="checkout-success__text"
						v-sanitize="$options.filters.fwgcmstext(notes)"
					/>
				</template>

				<template v-if="useDishes && !reusableDishDisabled">
					<h3
						class="checkout-success__title"
						v-html="$t('checkout.reusableDishes.successTitle')"
					/>
					<div
						class="checkout-success__text"
						v-html="$t('checkout.reusableDishes.successText')"
					/>
				</template>
			</template>

			<h3
				class="checkout-success__title"
				v-html="$t('checkout.success.order')"
			/>
			<template v-if="hasFeature('FEATURE_GROUP_ORDERS') && cartIsGroupOrder">
				<ul class="checkout-success__items">
					<template
						v-for="participant in groupOrderParticipantsEnhanced"
					>
						<li
							:key="participant.id"
							class="checkout-success__item checkout-success__item--participant"
							v-sanitize="participant.name"
						/>
						<li
							class="checkout-success__item"
							v-for="item in participant.items"
							:key="`item-${item.id}`"
						>
							<span
								class="checkout-success__item-quantity"
								v-html="item.quantity"
							/>
							<span class="checkout-success__item-name">
								<span v-html="item.displayName"/>
								<span
									v-if="item.allOptions"
									class="checkout-success__item-options"
									v-html="item.allOptions"
								/>
							</span>
							<span
								class="checkout-success__item-price"
								v-html="$options.filters.fwgpricecent(item.originalTotal, 'short')"
							/>
						</li>
					</template>
				</ul>
			</template>
			<template v-else>
				<ul class="checkout-success__items">
					<li
						class="checkout-success__item"
						v-for="item in itemsEnhanced"
						:key="`item-${item.id}`"
					>
						<span
							class="checkout-success__item-quantity"
							v-html="item.quantity"
						/>
						<span class="checkout-success__item-name">
							<span v-html="item.displayName"/>
							<span
								v-if="item.allOptions"
								class="checkout-success__item-options"
								v-html="item.allOptions"
							/>
						</span>
						<span
							class="checkout-success__item-price"
							v-html="$options.filters.fwgpricecent(item.originalTotal, 'short')"
						/>
					</li>
				</ul>
			</template>

			<template v-if="additionalItems.length">
				<h3
					class="checkout-success__title"
					v-html="$t('checkout.success.additionalProducts')"
				/>
				<ul class="checkout-success__items">
					<li
						class="checkout-success__item"
						v-for="item in additionalItems"
						:key="`item-${item.id}`"
					>
						<span
							class="checkout-success__item-quantity"
							v-html="item.quantity"
						/>
						<span
							class="checkout-success__item-name"
							v-html="item.name"
						/>
						<span
							class="checkout-success__item-price"
							v-html="$options.filters.fwgpricecent(item.originalTotal, 'short')"
						/>
					</li>
				</ul>
			</template>

			<div class="checkout-success__summary">
				<div
					class="checkout-success__summary-row checkout-success__summary-row--discount"
					v-for="discount in discounts"
					:key="discount.name"
				>
					<span
						class="checkout-success__summary-row-title"
						v-html="discount.name"
					/>
					<span
						class="checkout-success__summary-row-price"
						v-html="$options.filters.fwgpricecent(discount.amount.current, 'short')"
					/>
				</div>

				<div
					v-if="isDelivery && deliveryPrice"
					class="checkout-success__summary-row checkout-success__summary-row--shipping"
				>
					<span
						class="checkout-success__summary-row-title"
						v-html="$t('checkout.success.deliveryFee')"
					/>
					<span
						class="checkout-success__summary-row-price"
						v-html="$options.filters.fwgpricecent(deliveryPrice, 'short')"
					/>
				</div>

				<div
					v-if="cartCo2Compensation"
					class="checkout-success__summary-row checkout-success__summary-row--co2"
				>
					<span
						class="checkout-success__summary-row-title"
						v-html="$t('checkout.success.co2Compensation')"
					/>
					<span
						class="checkout-success__summary-row-price"
						v-html="$options.filters.fwgpricecent(cartCo2CompensationAmount, 'short')"
					/>
				</div>

				<div
					v-if="cartTip"
					class="checkout-success__summary-row checkout-success__summary-row--tip"
				>
					<span
						class="checkout-success__summary-row-title"
						v-html="$t('checkout.success.tip')"
					/>
					<span
						class="checkout-success__summary-row-price"
						v-html="$options.filters.fwgpricecent(cartTip, 'short')"
					/>
				</div>

				<div
					class="checkout-success__summary-row checkout-success__summary-row--total"
				>
					<span
						class="checkout-success__summary-row-title"
						v-html="$t('checkout.success.total')"
					/>
					<span
						class="checkout-success__summary-row-price"
						v-html="$options.filters.fwgpricecent(total, 'short')"
					/>
				</div>
			</div>

			<template v-if="hasFeature('FEATURE_TIP_VARIANTS') && orderTipSurveyUrl">
				<div class="checkout-success__block">
					<h3
						class="checkout-success__block-title"
						v-html="$t('checkout.success.tipSurvey.title')"
					/>
					<div
						class="checkout-success__block-text"
						v-html="$t('checkout.success.tipSurvey.text')"
					/>
					<Button
						level="primary"
						:text="$t('checkout.success.tipSurvey.button')"
						:href="orderTipSurveyUrl"
						target="_blank"
						arrow
						small
					/>
				</div>
			</template>

			<template v-if="orderSurveyUrl">
				<div class="checkout-success__block">
					<h3
						class="checkout-success__block-title"
						v-html="$t('checkout.success.survey.title')"
					/>
					<div
						class="checkout-success__block-text"
						v-html="$t('checkout.success.survey.text')"
					/>
					<Button
						level="primary"
						:text="$t('checkout.success.survey.button')"
						:href="orderSurveyUrl"
						target="_blank"
						arrow
						small
					/>
				</div>
			</template>

			<template v-if="hasFeature('FEATURE_ORDER_NPS') && orderNpsSurveyUrl">
				<div class="checkout-success__block">
					<h3
						class="checkout-success__block-title"
						v-html="$t('checkout.success.nps.title')"
					/>
					<div
						class="checkout-success__block-text"
						v-html="$t('checkout.success.nps.text')"
					/>
					<Button
						level="primary"
						:text="$t('checkout.success.nps.button')"
						:href="orderNpsSurveyUrl"
						target="_blank"
						arrow
						small
					/>
				</div>
			</template>

			<div
				v-if="isOrderTypeMain || isOrderTypeGastroKurier"
				class="checkout-success__loader"
			>
				<div class="checkout-success__loader-wrapper">
					<h1
						class="checkout-success__headline"
						v-html="$t('checkout.success.title')"
					/>
					<div class="checkout-success__loader-image">
						<img
							class="checkout-success__loader-image-element"
							:src="themeImagePath(`biker.gif`)"
							loading="eager"
						>
					</div>
					<h2
						class="checkout-success__subheadline"
						v-html="$options.filters.fwgmark($t(`checkout.success.loader.${themeName}`))"
					/>
				</div>
			</div>
		</template>
		<template v-else>
			<h1
				class="checkout-success__headline"
				v-html="$t('checkout.success.titleNotFound')"
			/>
			<div
				class="checkout-success__text"
				v-html="$t('checkout.success.textNotFound')"
			/>
			<Button
				level="primary"
				:text="$t('checkout.success.homeButton')"
				:href="`/${this.language}`"
			/>
		</template>

	</div>
</template>

<script>
import { mapGetters } from "vuex"
import {getUrlParams} from '@/utils/urlParams';
import CartOrderMixin from '@/mixins/CartOrderVueMixin';
import AccountReferral from '@/components/AccountReferral'
import Button from '@/components/Button'
import { createAdtTracking, createGtmTracking } from '@/helpers/TrackingHelpers'
import LiveOrderStates from "@/components/LiveOrderStates"

export default {
	mixins: [
		CartOrderMixin,
	],
	components: {
		AccountReferral,
		Button,
		LiveOrderStates,
	},
	data() {
		return {
		}
	},
	computed: {
		...mapGetters('loader', [
			'isLoading',
		]),
		...mapGetters('order', [
			'itemsWithoutAdditional',
			'additionalItems',
			'channelName',
			'cartDeliveryTime',
			'cartOrderNow',
			'shippingMethod',
			'payments',
			'total',
			'billingAddress',
			'shippingAddress',
			'dropoffType',
			'notes',
			'useDishes',
			'discounts',
			'cartCo2Compensation',
			'cartCo2CompensationAmount',
			'cartTip',
			'orderStates',
			'deliveryPrice',
			'orderType',
			'tokenValue',
			'cartOutdoorDeliveryPoint',
			'orderSurveyUrl',
			'orderNpsSurveyUrl',
			'orderTipSurveyUrl',
			'cartIsGroupOrder',
		]),
		...mapGetters('grouporder', [
			'groupOrderParticipant',
			'groupOrderIsOwner',
			'groupOrderParticipants',
		]),
		loading: {
			set(value) {
				this.$store.dispatch('loader/setLoading', value);
				this.$emit('loading', value);
			},
			get() {
				return this.isLoading;
			}
		},
		deliveryDate() {
			return this.cartDeliveryTime?.format('L')
		},
		deliveryTime() {
			return this.cartOrderNow
				? this.$t('checkout.success.orderNow')
				: this.cartDeliveryTime?.format('LT')
		},
		itemsEnhanced() {
			return this.itemsWithoutAdditional
				.reduce((items, item) => {
					if (!item.isTip && !item.isAdditional) {
						if (item.isEditable || item.isConfigurable) {

							item.allOptions = item.children
								.map(child => child.name)
								.join(', ')
						}

						items.push(item)
					}
					return items
				}, [])
		},
		groupOrderParticipantsEnhanced() {
			return this.groupOrderParticipants.map(participant => {
				return {
					...participant,
					isCurrentUser: participant.id === this.groupOrderParticipant?.id,
					items: this.itemsEnhanced.filter(item =>
						(item.groupOrderParticipantId === participant.id) ||
						(!item.groupOrderParticipantId && participant.isOwner)
					)
				}
			})
		},
		outdoorDeliveryAddress() {
			if (!this.isOutdoorDelivery) {
				return
			}
			const { title, street, streetNumber, postcode, city, latitude, longitude } = this.cartOutdoorDeliveryPoint
			const lines = []
			lines.push(title)
			lines.push([street, streetNumber].join(' '))
			lines.push([postcode, city].join(' '))
			// lines.push(`<a href="http://www.google.com/maps/place/${latitude},${longitude}" target="_blank">${this.$t('checkout.outdoorDelivery.googleMapsLink')}</a>`)
			return lines.join('<br>')
		}
	},
	methods: {
		formatAddress(address) {
			if (address) {

				let formattedAddress = [
					`${address.firstName} ${address.lastName}`
				];

				if (address.street || address.streetNumber) {
					formattedAddress.push(`${address.street || ''} ${address.streetNumber || ''}`);
				}

				if (address.postcode || address.city) {
					formattedAddress.push(`${address.postcode || ''} ${address.city || ''}`);
				}

				if (address.phoneNumber) {
					formattedAddress.push(`☎ ${address.phoneNumber}`);
				}

				if (address.company) {
					formattedAddress.unshift(address.company);
				}

				if (address.details) {
					formattedAddress.push(`<small>${address.details}</small>`);
				}

				return formattedAddress.join('<br>');
			}
		}
	},
	async created() {
		this.loading = true;
		let {tokenValue: token, type} = getUrlParams();

		this.type = type;

		await this.$store.dispatch('initializeCheckoutSuccess', { token })

		if (this.tokenValue) {
			createAdtTracking({
				...await this.$store.dispatch('order/getAdtTracking'),
				adtAp: this.$root.options?.adtAp || '',
				adtTp: this.$root.options?.adtTp || '',
			})
			createGtmTracking({
				...await this.$store.dispatch('order/getGtmTracking'),
				gtmId: this.$root.options?.gtmId
			})
		}

		this.loading = false;
	}
}
</script>

<style>

</style>
